import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.section`
  background: #111827;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  border: 1px solid #374151;
  background: #020617;
  color: #f9fafb;
`;

const ReadOnlyText = styled.div`
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background: #020617;
  border: 1px solid #374151;
  color: #e5e7eb;
`;

const StatusText = styled.span`
  font-size: 0.9rem;
  color: ${({ $verified }) => ($verified ? '#22c55e' : '#f97316')};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const Button = styled.button`
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  background: #6366f1;
  color: #f9fafb;
  transition: background 0.2s ease-in-out;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #4f46e5;
  }
`;

const Message = styled.p`
  font-size: 0.9rem;
  margin-top: 0.5rem;
  color: ${({ $type }) => ($type === 'error' ? '#f87171' : '#4ade80')};
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
`;

const SecondaryLinkButton = styled(Link)`
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  border: 1px solid #4b5563;
  background: transparent;
  color: #e5e7eb;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
`;

const AccountSettings = () => {
  const { user, updateDetails, updatePassword } = useAuth();

  const [profileForm, setProfileForm] = useState({
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    phone: user?.profile?.phone || '',
    studioName: user?.profile?.studioName || '',
    instagramHandle: user?.profile?.instagramHandle || '',
    facebookHandle: user?.profile?.facebookHandle || '',
    tiktokHandle: user?.profile?.tiktokHandle || '',
    website: user?.profile?.website || '',
    bookingLink: user?.profile?.bookingLink || '',
    hourlyRate: user?.profile?.hourlyRate || '',
    shopMinimum: user?.profile?.shopMinimum || '',
    depositAmount: user?.profile?.depositAmount || '',
    halfDayRate: user?.profile?.halfDayRate || '',
    fullDayRate: user?.profile?.fullDayRate || '',
    workingHours: user?.profile?.workingHours || '',
  });
  const [preferredStyles, setPreferredStyles] = useState(
    Array.isArray(user?.profile?.preferredStyles) && user.profile.preferredStyles.length
      ? user.profile.preferredStyles
      : ['']
  );
  const [profileStatus, setProfileStatus] = useState(null); // 'success' | 'error' | null
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordStatus, setPasswordStatus] = useState(null); // 'success' | 'error' | null
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStyleChange = (index, value) => {
    setPreferredStyles(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddStyle = () => {
    setPreferredStyles(prev => [...prev, '']);
  };

  const handleRemoveStyle = (index) => {
    setPreferredStyles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileStatus(null);
    setProfileMessage('');
    setProfileSaving(true);

    try {
      const numericFields = ['hourlyRate', 'shopMinimum', 'depositAmount', 'halfDayRate', 'fullDayRate'];
      const numericOnly = /^\d+(\.\d+)?$/;

      for (const field of numericFields) {
        const raw = (profileForm[field] || '').toString().trim();
        if (raw && !numericOnly.test(raw)) {
          setProfileStatus('error');
          setProfileMessage('Only numbers can be used for rates, minimums, and sessions.');
          setProfileSaving(false);
          return;
        }
      }

      const cleanedStyles = preferredStyles
        .map(s => s.trim())
        .filter(Boolean);

      await updateDetails({
        profile: {
          ...profileForm,
          preferredStyles: cleanedStyles,
        },
      });
      setProfileStatus('success');
      setProfileMessage('Profile updated successfully.');
    } catch (err) {
      const message = err?.message || err?.data?.message || 'Failed to update profile.';
      setProfileStatus('error');
      setProfileMessage(message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordStatus(null);
    setPasswordMessage('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordStatus('error');
      setPasswordMessage('Please fill in all password fields.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus('error');
      setPasswordMessage('New passwords do not match.');
      return;
    }

    setPasswordSaving(true);

    try {
      await updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordStatus('success');
      setPasswordMessage('Password updated successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      const message = err?.message || err?.data?.message || 'Failed to update password.';
      setPasswordStatus('error');
      setPasswordMessage(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Container>
      <FooterRow style={{ justifyContent: 'center', marginTop: 0, marginBottom: '0.75rem' }}>
        <SecondaryLinkButton to="/dashboard">Return to dashboard</SecondaryLinkButton>
      </FooterRow>

      <Section>
        <SectionTitle>Account Overview</SectionTitle>
        <FieldRow>
          <Label>Email</Label>
          <ReadOnlyText>{user?.email || 'Unknown email'}</ReadOnlyText>
        </FieldRow>
        <FieldRow style={{ marginTop: '0.75rem' }}>
          <Label>Email Status</Label>
          <StatusText $verified={!!user?.emailVerified}>
            {user?.emailVerified ? 'Verified' : 'Not verified'}
          </StatusText>
        </FieldRow>
      </Section>

      <Section>
        <SectionTitle>Profile Details</SectionTitle>
        <Form onSubmit={handleProfileSubmit}>
          <FieldRow>
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              name="firstName"
              value={profileForm.firstName}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              name="lastName"
              value={profileForm.lastName}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              name="phone"
              value={profileForm.phone}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="studioName">Artist / studio name</Label>
            <Input
              id="studioName"
              name="studioName"
              value={profileForm.studioName}
              onChange={handleProfileChange}
            />
          </FieldRow>

          <FieldRow>
            <Label>Preferred styles</Label>
            {preferredStyles.map((style, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <Input
                  name={`preferredStyle-${index}`}
                  placeholder={index === 0 ? 'e.g. black & grey, fine line' : 'Another style'}
                  value={style}
                  onChange={e => handleStyleChange(index, e.target.value)}
                />
                {preferredStyles.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => handleRemoveStyle(index)}
                    style={{ padding: '0.4rem 0.6rem', background: '#4b5563' }}
                  >
                    −
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              onClick={handleAddStyle}
              style={{ marginTop: '0.25rem', background: '#374151' }}
            >
              + Add style
            </Button>
          </FieldRow>

          <FieldRow>
            <Label htmlFor="instagramHandle">Instagram handle</Label>
            <Input
              id="instagramHandle"
              name="instagramHandle"
              placeholder="@yourhandle"
              value={profileForm.instagramHandle}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="facebookHandle">Facebook page / handle</Label>
            <Input
              id="facebookHandle"
              name="facebookHandle"
              placeholder="@yourfacebook or https://facebook.com/yourpage"
              value={profileForm.facebookHandle}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="tiktokHandle">TikTok handle</Label>
            <Input
              id="tiktokHandle"
              name="tiktokHandle"
              placeholder="@yourtiktok"
              value={profileForm.tiktokHandle}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              name="website"
              placeholder="https://yourstudio.com"
              value={profileForm.website}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="bookingLink">External booking link (optional)</Label>
            <Input
              id="bookingLink"
              name="bookingLink"
              placeholder="https://yourbookingtool.com/your-page"
              value={profileForm.bookingLink}
              onChange={handleProfileChange}
            />
          </FieldRow>

          <FieldRow>
            <Label htmlFor="hourlyRate">Hourly rate</Label>
            <Input
              id="hourlyRate"
              name="hourlyRate"
              placeholder="$150/hr"
              value={profileForm.hourlyRate}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="shopMinimum">Shop minimum</Label>
            <Input
              id="shopMinimum"
              name="shopMinimum"
              placeholder="$100 minimum"
              value={profileForm.shopMinimum}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="depositAmount">Deposit to book an appointment</Label>
            <Input
              id="depositAmount"
              name="depositAmount"
              placeholder="$100 deposit"
              value={profileForm.depositAmount}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="halfDayRate">Half-day session price</Label>
            <Input
              id="halfDayRate"
              name="halfDayRate"
              placeholder="$500 (approx. 4 hours)"
              value={profileForm.halfDayRate}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="fullDayRate">Full-day session price</Label>
            <Input
              id="fullDayRate"
              name="fullDayRate"
              placeholder="$900 (full day)"
              value={profileForm.fullDayRate}
              onChange={handleProfileChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="workingHours">Working hours (if applicable)</Label>
            <Input
              id="workingHours"
              name="workingHours"
              placeholder="e.g. Mon–Fri, 11am–7pm"
              value={profileForm.workingHours}
              onChange={handleProfileChange}
            />
          </FieldRow>

          <ButtonRow>
            <Button type="submit" disabled={profileSaving}>
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </ButtonRow>

          {profileStatus && (
            <Message $type={profileStatus === 'error' ? 'error' : 'success'}>
              {profileMessage}
            </Message>
          )}
        </Form>
      </Section>

      <Section>
        <SectionTitle>Change Password</SectionTitle>
        <Form onSubmit={handlePasswordSubmit}>
          <FieldRow>
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
            />
          </FieldRow>

          <ButtonRow>
            <Button type="submit" disabled={passwordSaving}>
              {passwordSaving ? 'Updating...' : 'Update Password'}
            </Button>
          </ButtonRow>

          {passwordStatus && (
            <Message $type={passwordStatus === 'error' ? 'error' : 'success'}>
              {passwordMessage}
            </Message>
          )}
        </Form>
      </Section>

      <FooterRow>
        <SecondaryLinkButton to="/dashboard">Return to dashboard</SecondaryLinkButton>
      </FooterRow>
    </Container>
  );
};

export default AccountSettings;
